import { useEffect, useState } from "react";
import { Pencil, Check, X, RotateCcw } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

// Inline admin-editable text. Falls back to `children` (the i18n default) and
// stores per-language overrides keyed `${k}.${lang}` in the site_content store.
// Pens/outlines only appear while the admin has "Editor mode" switched on.
export default function EditableText({
  k,
  children,
  as: Tag = "span",
  className = "",
  multiline = false,
  inputClassName = "",
}) {
  const { content, save, reset, editorMode } = useSiteContent();
  const { lang } = useI18n();
  const storageKey = `${k}.${lang}`;
  const fallback = typeof children === "string" ? children : "";
  const overridden = Object.prototype.hasOwnProperty.call(content, storageKey);
  const text = overridden ? content[storageKey] : fallback;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editorMode) setEditing(false);
  }, [editorMode]);

  const style = multiline ? { whiteSpace: "pre-line" } : undefined;

  async function commit() {
    setBusy(true);
    try {
      await save(storageKey, draft);
      toast.success("Saved");
      setEditing(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    setBusy(true);
    try {
      await reset(storageKey);
      toast.success("Restored default text");
      setEditing(false);
    } catch {
      toast.error("Reset failed");
    } finally {
      setBusy(false);
    }
  }

  if (!editorMode) {
    return (
      <Tag className={className} style={style} data-testid={`editable-${k}`}>
        {text}
      </Tag>
    );
  }

  if (editing) {
    const Field = multiline ? "textarea" : "input";
    const onKeyDown = (e) => {
      if (e.key === "Escape") setEditing(false);
      if (e.key === "Enter" && (!multiline || e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        commit();
      }
    };
    return (
      <span
        className="block w-full my-1 normal-case tracking-normal font-sans text-left"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Field
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          rows={multiline ? Math.min(18, Math.max(4, draft.split("\n").length + Math.ceil(draft.length / 80) + 1)) : undefined}
          data-testid={`editable-input-${k}`}
          className={`block bg-[#121216] border border-amber-400/70 rounded-sm px-2 py-1.5 text-sm font-normal text-stone-100 w-full min-w-[200px] leading-relaxed focus:outline-none ${inputClassName}`}
        />
        <span className="flex flex-wrap items-center gap-2 mt-2 text-[10px] font-bold uppercase tracking-[0.18em]">
          <button
            type="button"
            disabled={busy}
            onClick={commit}
            data-testid={`editable-save-${k}`}
            className="btn-gold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm disabled:opacity-50"
          >
            <Check className="w-3.5 h-3.5" /> Save
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setDraft(text);
              setEditing(false);
            }}
            className="inline-flex items-center gap-1 border border-amber-500/30 text-stone-300 px-2.5 py-1.5 rounded-sm hover:text-stone-100"
          >
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          {overridden && (
            <button
              type="button"
              disabled={busy}
              onClick={restore}
              data-testid={`editable-reset-${k}`}
              className="inline-flex items-center gap-1 text-stone-500 hover:text-amber-300 px-1.5 py-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset to default
            </button>
          )}
          <span className="text-stone-600 font-normal normal-case tracking-normal">
            {lang.toUpperCase()} · {multiline ? "Ctrl+Enter to save" : "Enter to save"} · Esc to cancel
          </span>
        </span>
      </span>
    );
  }

  return (
    <Tag
      className={`group/edit relative cursor-text rounded-[2px] outline outline-1 outline-dashed outline-amber-400/25 hover:outline-amber-400/80 outline-offset-2 ${className}`}
      style={style}
      data-testid={`editable-${k}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDraft(text);
        setEditing(true);
      }}
      title="Click to edit"
    >
      {text}
      <Pencil
        data-testid={`editable-pencil-${k}`}
        className="inline-block ml-1.5 w-3 h-3 align-middle text-amber-400 opacity-60 group-hover/edit:opacity-100"
      />
    </Tag>
  );
}
